import { useCallback, useRef, useState, type FormEvent } from 'react'
import { Btn } from './Btn'
import { Honeypot, HONEYPOT_FIELD } from '../forms/Honeypot'
import { Turnstile } from '../forms/Turnstile'
import { useOptionalT } from '../i18n/LocaleProvider'

export interface FormField {
  /** Submitted key, and the second half of the input's DOM id. */
  name: string
  placeholder: string
  /** `textarea` renders a `<textarea>`; everything else an `<input>` of that type. */
  type?: 'text' | 'email' | 'tel' | 'textarea'
  /**
   * Bootstrap column class. Omit for the half-width default the row provides; pass
   * `col-12` for a field that takes the row.
   */
  colClassName?: string
  /**
   * A starting value the visitor can still edit — for a field arriving pre-filled from
   * elsewhere, such as the resort's search handing over the thing that was selected.
   *
   * `defaultValue`, not `value`: the inputs here are uncontrolled and read back through
   * `FormData` on submit, so a `value` would freeze the field. Editable matters — somebody
   * who selected the wrong room should correct it, not start again.
   */
  defaultValue?: string
}

export interface ContactFormProps {
  /**
   * Namespace for the inputs' DOM ids, e.g. `enquiry` → `ul-enquiry-email`. Two forms on
   * one page with the same prefix would produce duplicate ids and break every `<label>`
   * association on the second one.
   */
  id: string
  fields: FormField[]
  submitLabel: string
  /** Centre the submit button — right for a form that is the whole section. */
  centered?: boolean
  className?: string
  /**
   * Called with the submitted values, keyed by field name.
   *
   * **Most forms in KKF are currently inert**: submission is prevented and forwarded here,
   * and nothing posts anywhere. That is deliberate until there is an endpoint — a form
   * that appears to send and does not is worse than one that visibly cannot. Wiring this
   * up is the only change needed at the surface.
   *
   * The honeypot field arrives in `values` under `HONEYPOT_FIELD`, and the Turnstile token
   * as `verification`. Both must be posted to the server: a check that only the browser
   * runs is a check a script never runs, because a script posts to the endpoint rather
   * than typing into a page.
   */
  onSubmit?: (values: Record<string, string>) => void
  /**
   * Cloudflare Turnstile's site key — `resolveTurnstileSiteKey(import.meta.env)`.
   *
   * **Omitted or empty renders no widget**, which is the state today: there is no Turnstile
   * account, and a form that showed a widget stuck loading would be worse than one with no
   * widget at all. The honeypot below is always rendered, because it costs the visitor
   * nothing and needs no account.
   */
  turnstileSiteKey?: string
  /** `auto` follows the page. Passed through to `Turnstile`. */
  turnstileTheme?: 'auto' | 'light' | 'dark'
}

/**
 * The `.ul-contact-form` block — the one form shape in the system.
 *
 * Four apps ask for different things (a message, a booking enquiry, a business
 * application), and each was rebuilding the same row of `.form-group`s to do it. Fields
 * are declared as data here instead, so the markup, the inert-submit policy and the id
 * discipline all have exactly one definition.
 *
 * ## Both anti-spam measures are here, not at each call site (2026-09-21)
 *
 * CLAUDE.md's rule is that **every unauthenticated form carries two** — a honeypot and
 * Turnstile — and until this change exactly one form on the platform did: Partners' join,
 * which builds its own markup. The Foundation's contact form, the garage's and the
 * resort's enquiry all render through here and carried neither, so the rule was true of the
 * one form somebody had written by hand and false of the three that used the shared block.
 *
 * Putting them in the block rather than in each page is the whole point of the block. A
 * fourth app mounting this gets both without knowing they exist, and the day these forms
 * are wired to an endpoint, `onSubmit` already receives what the server checks.
 *
 * **Neither measure changes what a visitor sees today.** The honeypot is off-screen by
 * design, and `Turnstile` renders nothing without a site key — and there is no Turnstile
 * account yet. See `config/turnstile.ts` for why both ends read their own half of the pair.
 */
export function ContactForm({
  id,
  fields,
  submitLabel,
  centered = false,
  className,
  onSubmit,
  turnstileSiteKey,
  turnstileTheme = 'auto',
}: ContactFormProps) {
  const t = useOptionalT()
  const [token, setToken] = useState<string | null>(null)
  const honeypot = useRef<HTMLInputElement | null>(null)

  // Stable, so the widget is not torn down and re-rendered on every parent render — which
  // would reset a check the visitor has already passed. `Turnstile`'s own header says so.
  const onToken = useCallback((next: string | null) => setToken(next), [])

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const data = new FormData(e.currentTarget)
    const values: Record<string, string> = {}
    for (const field of fields) {
      const value = data.get(field.name)
      values[field.name] = typeof value === 'string' ? value : ''
    }
    // Read from the ref rather than from `FormData`, so a caller that renames a field
    // cannot accidentally shadow it, and so the key is the one constant both ends share.
    values[HONEYPOT_FIELD] = honeypot.current?.value ?? ''
    values.verification = token ?? ''
    onSubmit?.(values)
  }

  return (
    <form action="#" className={className ? `ul-contact-form ul-form ${className}` : 'ul-contact-form ul-form'} onSubmit={handleSubmit}>
      <div className="row row-cols-2 row-cols-xxs-1 ul-bs-row">
        {fields.map((field) => (
          // The key carries `defaultValue`, so a field whose pre-filled value changes gets a
          // fresh input rather than a stale one: React ignores a changed `defaultValue` on an
          // existing uncontrolled input, which would have made the resort's Select silently
          // fail on the second click.
          <div
            className={field.colClassName ?? 'col'}
            key={field.defaultValue ? `${field.name}-${field.defaultValue}` : field.name}
          >
            <div className="form-group">
              {field.type === 'textarea' ? (
                <textarea
                  name={field.name}
                  id={`ul-${id}-${field.name}`}
                  placeholder={field.placeholder}
                  defaultValue={field.defaultValue}
                />
              ) : (
                <input
                  type={field.type ?? 'text'}
                  name={field.name}
                  id={`ul-${id}-${field.name}`}
                  placeholder={field.placeholder}
                  defaultValue={field.defaultValue}
                />
              )}
            </div>
          </div>
        ))}
        {/*
          The two anti-spam measures, in the order they cost.

          The honeypot is free and always rendered — it is off-screen rather than
          `display: none`, so somebody using a screen reader is not the only person who
          fills it in and the only person silently refused. Turnstile renders nothing
          without a site key. See both components' headers.
        */}
        <div className="col-12">
          <Honeypot
            id={id}
            label={t('ul.form.honeypot')}
            inputRef={(element) => (honeypot.current = element)}
          />
          <Turnstile
            siteKey={turnstileSiteKey}
            onToken={onToken}
            label={t('ul.form.turnstile.label')}
            errorLabel={t('ul.form.turnstile.error')}
            theme={turnstileTheme}
          />
        </div>

        <div className={centered ? 'col-12 text-center' : 'col-12'}>
          <Btn type="submit" icon="fast-forward-double-right-arrows-symbol">
            {submitLabel}
          </Btn>
        </div>
      </div>
    </form>
  )
}
